import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { useTheme, alpha } from "@mui/material/styles";
import { adminSurfaceSx } from "./adminStyles";
import { adminRequest } from "../../utils/adminApi";

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

// Admin chat-timeline dialog (hybra #422). Given a feedback-triage item's
// conversation id + user email, fetches the full coach conversation via the
// functions/api/admin/getConversationTimeline proxy → worker
// GET /api/admin/conversation-timeline and renders it oldest-first as a
// user-bubble → coach-bubble timeline. A debug-metadata header exposes the chat
// id, user, and message id, each with a copy button so admins can extract the
// identifiers for AI-development feedback. The worker enforces admin auth
// server-side; a 404/empty conversation renders a graceful empty state.
//
// `userEmail` may be undefined before the worker lane (additive item.user_email
// field) ships — callers should keep the item non-clickable in that case, but
// the dialog also guards the fetch so it never fires a broken request.
export default function ChatTimelineDialog({
  open,
  onClose,
  conversationId,
  userEmail,
  messageId,
}) {
  const theme = useTheme();
  // env-aware: prod uses the authSlice token as today; staging uses the held
  // staging token and adds the routing-only env param (adminRequest).
  const { env, token } = useSelector(adminRequest);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // worker#425: keep the WHOLE payload, not just turns — `orphan_runs` (coach runs whose
  // turn was never persisted = failed turns) lives at the top level and used to be discarded.
  const [data, setData] = useState(null);
  const [snack, setSnack] = useState("");

  // Memoized so their identity is stable across renders — otherwise the copyForLlm
  // useCallback below (which depends on them) would be rebuilt every render.
  const turns = useMemo(() => (data ? (data.turns ?? []) : null), [data]);
  const orphanRuns = useMemo(() => (data ? (data.orphan_runs ?? []) : []), [data]);

  const copyValue = useCallback(async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setSnack(`${label} copied`);
    } catch (err) {
      console.error("Clipboard write failed:", err);
      setSnack("Copy failed — clipboard unavailable.");
    }
  }, []);

  // Bundle the debug handles + the full conversation into ONE self-contained JSON
  // blob an operator can paste straight into an LLM to debug a bad coach turn.
  // turn.prompt/response are relabeled user/coach so the thread reads unambiguously.
  const copyForLlm = useCallback(() => {
    const payload = {
      conversation_id: conversationId ?? null,
      user_email: userEmail ?? null,
      flagged_message_id: messageId ?? null,
      turn_count: (turns ?? []).length,
      turns: (turns ?? []).map((t) => ({
        created_at: t.created_at ?? null,
        user: t.prompt ?? "",
        coach: t.response ?? "",
        // worker#425: what the coach actually DID this turn, not just what it said. `run: null`
        // means no coach_run joined (a legacy turn) — deliberately distinct from a run that
        // called no tools, which is itself the signal behind worker#420.
        run: t.run ?? null,
        trace_id: t.trace_id ?? null,
        message_id: t.message_id ?? null,
      })),
      // Runs whose turn never persisted — the coach ran, then the turn died before it was saved.
      orphan_runs: orphanRuns,
    };
    copyValue(JSON.stringify(payload, null, 2), "Conversation JSON");
  }, [conversationId, userEmail, messageId, turns, orphanRuns, copyValue]);

  useEffect(() => {
    if (!open) return;

    // Guard: without a user email the worker query is malformed — surface a
    // clear state rather than firing a broken request (pre-deploy tolerance).
    if (!userEmail || !conversationId) {
      setLoading(false);
      setError(null);
      setData({ turns: [], orphan_runs: [] });
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    const params = new URLSearchParams({
      user: userEmail,
      id: conversationId,
    });
    // env is routing-only; the proxy strips it before forwarding to the worker.
    if (env === "staging") params.set("env", "staging");

    axios
      .get(`/api/admin/getConversationTimeline?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((result) => {
        if (cancelled) return;
        setData(result.data ?? { turns: [], orphan_runs: [] });
      })
      .catch((err) => {
        if (cancelled) return;
        // 404 = conversation not found/empty → graceful empty state, not error.
        if (err?.response?.status === 404) {
          setData({ turns: [], orphan_runs: [] });
        } else {
          console.error("Failed to load conversation timeline:", err);
          setError("Failed to load the conversation timeline.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, userEmail, conversationId, token, env]);

  const metaRow = (label, value) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: MONO,
            color: theme.palette.text.primary,
            wordBreak: "break-all",
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
      <IconButton
        size="small"
        aria-label={`Copy ${label}`}
        disabled={!value}
        onClick={() => copyValue(value, label)}
        sx={{ flexShrink: 0 }}
      >
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Box>
  );

  const bubble = (who, text, accent) => (
    <Box
      sx={{
        maxWidth: "85%",
        alignSelf: who === "user" ? "flex-end" : "flex-start",
        borderRadius: 2,
        px: 1.5,
        py: 1,
        backgroundColor: alpha(accent, 0.14),
        border: `1px solid ${alpha(accent, 0.35)}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: accent, fontWeight: 700, display: "block", mb: 0.25 }}
      >
        {who === "user" ? "User" : "Coach"}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.primary, whiteSpace: "pre-wrap" }}
      >
        {text || "(empty)"}
      </Typography>
    </Box>
  );

  // Map a coach_run outcome to a MUI colour. 'ok' is the happy path; 'invariant' = the honesty
  // audit tripped; 'error' = the run threw and the user got a fallback reply.
  const outcomeColor = (outcome) => {
    if (outcome === "ok") return "success";
    if (outcome === "invariant") return "warning";
    if (outcome === "error") return "error";
    return "default";
  };

  // worker#425: what the coach actually DID on a turn — the tools it called, in call order.
  //
  // The three states are deliberately distinct, because conflating them is exactly what let
  // worker#420 hide for so long:
  //   run == null        → no coach_run joined: a LEGACY turn (predating #425). We do not KNOW what
  //                        it called. Rendered as "unknown" — never as "no tools".
  //   tools.length === 0 → the coach called NOTHING. A real, reportable signal: it is how a
  //                        prescribed weight can be pure invention (no get_exercise_stats read).
  //   tools.length > 0   → one chip per call; green when ok, red when the tool failed.
  const runRow = (run) => {
    if (!run) {
      return (
        <Box data-testid="chat-timeline-run-unknown" sx={{ mt: 0.75 }}>
          <Chip
            size="small"
            variant="outlined"
            label="tools unknown (legacy turn)"
            sx={{
              fontFamily: MONO,
              color: theme.palette.text.secondary,
              borderColor: alpha(theme.palette.text.secondary, 0.35),
            }}
          />
        </Box>
      );
    }

    const tools = run.tools ?? [];
    return (
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        data-testid="chat-timeline-run"
        sx={{ mt: 0.75 }}
      >
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontWeight: 700,
          }}
        >
          Tools
        </Typography>

        {tools.length === 0 ? (
          // The coach answered without calling anything. On a turn that PRESCRIBES loads, this is
          // the fingerprint of an ungrounded (invented) number — worker#420.
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label="no tools called"
            data-testid="chat-timeline-no-tools"
            sx={{ fontFamily: MONO }}
          />
        ) : (
          tools.map((t, ti) => (
            <Chip
              key={`${t.name}-${ti}`}
              size="small"
              color={t.ok ? "success" : "error"}
              variant="outlined"
              data-testid="chat-timeline-tool"
              label={`${t.name}${t.ok ? "" : " ✗"} · ${t.ms ?? 0}ms`}
              sx={{ fontFamily: MONO }}
            />
          ))
        )}

        {run.outcome && (
          <Chip
            size="small"
            color={outcomeColor(run.outcome)}
            variant="outlined"
            label={run.outcome}
            data-testid="chat-timeline-outcome"
            sx={{ fontFamily: MONO }}
          />
        )}

        {run.forced_tool && (
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, fontFamily: MONO }}
          >
            forced={run.forced_tool}
          </Typography>
        )}
        {typeof run.duration_ms === "number" && (
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, fontFamily: MONO }}
          >
            {run.duration_ms}ms
          </Typography>
        )}
        {run.error && (
          <Typography
            variant="caption"
            sx={{ color: theme.palette.error.main, fontFamily: MONO }}
          >
            {run.error.kind ?? "error"}: {run.error.message ?? ""}
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      slotProps={{ paper: { sx: adminSurfaceSx(theme) } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Chat timeline
        </Typography>
        <IconButton aria-label="Close" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Debug metadata — copyable identifiers for AI-development feedback. */}
        <Box
          data-testid="chat-timeline-debug"
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha("#ffffff", 0.1)}`,
            backgroundColor: alpha("#ffffff", 0.03),
            p: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 700,
              }}
            >
              Debug
            </Typography>
            {/* One-click bundle of the ids + full conversation as JSON, ready to
                paste into an LLM to debug the flagged turn. */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={copyForLlm}
              disabled={loading || turns === null}
            >
              Copy for LLM
            </Button>
          </Box>
          <Stack spacing={0.5}>
            {metaRow("Chat id", conversationId)}
            {metaRow("User", userEmail)}
            {metaRow("Message id", messageId)}
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        ) : turns && turns.length > 0 ? (
          <Stack spacing={2}>
            {turns.map((turn, i) => (
              <Box
                key={`${turn.created_at ?? "turn"}-${i}`}
                data-testid="chat-timeline-turn"
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.5,
                    fontFamily: MONO,
                  }}
                >
                  {turn.created_at
                    ? dayjs(turn.created_at).format("MMM D, YYYY HH:mm")
                    : ""}
                </Typography>
                <Stack spacing={1}>
                  {bubble("user", turn.prompt, theme.palette.primary.main)}
                  {bubble("coach", turn.response, theme.palette.success.main)}
                </Stack>
                {/* worker#425: the tools this turn actually called. Reading the reply alone can't
                    tell you whether a prescribed weight was grounded in the user's history or
                    invented — this row can. */}
                {runRow(turn.run)}
                {i < turns.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}

            {/* Runs with NO persisted turn: the coach ran, then the turn died before it was saved
                (the route threw after the agent returned). The user saw an error; this panel used to
                show nothing at all for them. */}
            {orphanRuns.length > 0 && (
              <Box data-testid="chat-timeline-orphans" sx={{ mt: 1 }}>
                <Divider sx={{ mb: 2 }} />
                <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
                  {orphanRuns.length} failed turn
                  {orphanRuns.length === 1 ? "" : "s"} — the coach ran, but the
                  turn was never persisted.
                </Alert>
                <Stack spacing={1}>
                  {orphanRuns.map((run, oi) => (
                    <Box
                      key={run.trace_id ?? `orphan-${oi}`}
                      data-testid="chat-timeline-orphan-run"
                    >
                      {runRow(run)}
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontFamily: MONO,
                          display: "block",
                          mt: 0.25,
                        }}
                      >
                        trace={run.trace_id || "—"}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        ) : (
          <Alert
            severity="info"
            variant="outlined"
            data-testid="chat-timeline-empty"
          >
            Conversation not found or empty.
          </Alert>
        )}
      </DialogContent>
      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Dialog>
  );
}
